import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../lib/cart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bulkNote = (totalBags) => {
  if (totalBags === 0) return "Add coffee bags. 3 or more unlocks $17.99 per bag.";
  if (totalBags >= 3) return "3-pack pricing unlocked: $17.99 per bag.";
  const left = 3 - totalBags;
  return `Add ${left} more ${left === 1 ? "bag" : "bags"} of coffee to unlock $17.99/bag.`;
};

export const CartDrawer = ({ onJoin }) => {
  const { items, setQty, remove, totalBags, totalItems, unitPrice, subtotal, open, setOpen } = useCart();
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/shop/checkout`, {
        items: items.map((i) => ({ handle: i.id, quantity: i.qty })),
      });
      window.location.href = res.data.url;
    } catch (err) {
      if (err?.response?.status === 503) setUnavailable(true);
      else toast.error(err?.response?.data?.detail || "Checkout failed. Try again in a minute.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => { setOpen(false); setUnavailable(false); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-void/70 backdrop-blur-sm"
          onClick={close}
          data-testid="cart-overlay"
        >
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="cart-drawer"
            className="absolute right-0 top-0 h-full w-full max-w-md bg-ash border-l border-white/10 flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <h3 className="font-display font-black uppercase tracking-tight text-cream text-xl flex items-center gap-3">
                <ShoppingBag size={18} className="text-ember" /> Your Bag
                <span className="font-mono text-xs text-cream/65 font-normal">({totalItems} {totalItems === 1 ? "item" : "items"})</span>
              </h3>
              <button onClick={close} data-testid="cart-close-button" className="text-cream/75 hover:text-ember transition-colors">
                <X size={22} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-empty">
                <ShoppingBag size={40} className="text-cream/20" />
                <p className="font-mono text-sm text-cream/65">Your bag is empty. The gremlins are disappointed.</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                  {items.map((i) => (
                    <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex items-center gap-4 border border-white/10 rounded-lg p-4">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: i.accent }} />
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-extrabold uppercase text-cream text-sm truncate">{i.name}</p>
                        <p className="font-mono text-[10px] text-cream/45">
                          {(i.kind || "coffee") === "coffee" ? `12 oz whole bean · $${unitPrice.toFixed(2)}` : `Merch · $${(i.price || 0).toFixed(2)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setQty(i.id, i.qty - 1)} data-testid={`cart-dec-${i.id}`} className="w-7 h-7 rounded-full border border-white/15 text-cream/70 hover:border-ember hover:text-ember transition-colors flex items-center justify-center"><Minus size={12} /></button>
                        <span className="font-mono text-sm text-cream w-5 text-center" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                        <button onClick={() => setQty(i.id, i.qty + 1)} data-testid={`cart-inc-${i.id}`} className="w-7 h-7 rounded-full border border-white/15 text-cream/70 hover:border-ember hover:text-ember transition-colors flex items-center justify-center"><Plus size={12} /></button>
                      </div>
                      <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} className="text-cream/55 hover:text-ember transition-colors"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/10 px-6 py-5 space-y-4">
                  <p className="font-mono text-[11px] text-ember" data-testid="cart-bulk-note">
                    {bulkNote(totalBags)}
                  </p>
                  <div className="flex justify-between font-mono text-sm text-cream">
                    <span className="text-cream/75">Subtotal</span>
                    <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  {items.some((i) => i.kind === "merch") && (
                    <p data-testid="cart-merch-shipping-note" className="font-mono text-[11px] text-cream/65 leading-relaxed">
                      Heads up: merch ships separately from coffee, so your beans and your gear arrive as two packages.
                    </p>
                  )}

                  {unavailable ? (
                    <div className="bg-void border border-ember/40 rounded-lg p-4 space-y-3" data-testid="cart-unavailable">
                      <p className="font-mono text-xs text-cream/70 leading-relaxed">
                        Online checkout opens soon. Join the Coffee Club and we'll email you the second the shop goes live, with first dibs pricing.
                      </p>
                      <button
                        onClick={() => { close(); onJoin(); }}
                        data-testid="cart-join-club-button"
                        className="w-full bg-ember text-void font-mono text-xs uppercase tracking-widest py-3 rounded-full font-medium hover:bg-emberDark transition-colors"
                      >
                        Join the Coffee Club
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={checkout}
                      disabled={loading}
                      data-testid="cart-checkout-button"
                      className="w-full bg-ember text-void font-mono text-xs uppercase tracking-widest py-4 rounded-full font-medium hover:bg-emberDark transition-colors disabled:opacity-50"
                    >
                      {loading ? "Opening checkout..." : "Checkout →"}
                    </button>
                  )}
                </div>
              </>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
